import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { employeeService } from '../services/api';
import {
  Card, Button, Modal, FormField, Badge, SearchInput,
  Table, Pagination, EmptyState, Spinner, inputStyle,
} from '../components/common/UI';
import { formatPhone } from '../utils/format';
import '../components/common/styles.css';

export default function EmployeesPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'create' | {employee}
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['employees', page, search],
    queryFn: () => employeeService.list({ page, search }).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: statsData } = useQuery({
    queryKey: ['employee-stats'],
    queryFn: () => employeeService.stats().then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (d) => employeeService.create(d),
    onSuccess: () => {
      qc.invalidateQueries(['employees']);
      qc.invalidateQueries(['employee-stats']);
      qc.invalidateQueries(['dashboard-stats']);
      toast.success("Xodim muvaffaqiyatli qo'shildi!");
      setModal(null);
    },
    onError: (e) => {
      const msg = e.response?.data?.jshshir?.[0] ||
        e.response?.data?.detail ||
        "Xatolik yuz berdi";
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => employeeService.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries(['employees']);
      toast.success("Xodim ma'lumotlari yangilandi!");
      setModal(null);
    },
    onError: () => toast.error("Yangilashda xatolik"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => employeeService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries(['employees']);
      qc.invalidateQueries(['employee-stats']);
      toast.success("Xodim o'chirildi");
      setDeleteTarget(null);
    },
    onError: () => toast.error("O'chirishda xatolik"),
  });

  const columns = [
    {
      label: '#', key: 'id',
      render: (_, i) => (
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</span>
      ),
    },
    {
      label: 'Xodim', key: 'full_name',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--sky-400), var(--sky-600))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>
            {row.full_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{row.full_name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.position}</div>
          </div>
        </div>
      ),
    },
    {
      label: 'JSHSHIR', key: 'jshshir',
      render: (row) => (
        <code style={{
          fontSize: 12, background: 'var(--sky-50)',
          padding: '3px 8px', borderRadius: 4, color: 'var(--sky-700)',
          letterSpacing: '0.06em',
        }}>
          {row.jshshir}
        </code>
      ),
    },
    {
      label: 'Telefon', key: 'phone_number',
      render: (row) => (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {formatPhone(row.phone_number)}
        </span>
      ),
    },
    {
      label: 'Bot holati', key: 'is_registered',
      render: (row) => row.is_registered
        ? <Badge color="var(--emerald-500)" bg="var(--emerald-100)">✓ Ro'yxatdan o'tgan</Badge>
        : <Badge color="var(--amber-500)" bg="var(--amber-100)">⏳ Kutilmoqda</Badge>,
    },
    {
      label: 'Amallar', key: 'actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            variant="secondary" size="sm"
            onClick={() => setModal(row)}
            icon="✏️"
          >
            Tahrir
          </Button>
          <Button
            variant="danger" size="sm"
            onClick={() => setDeleteTarget(row)}
            icon="🗑"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Stats row */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        {[
          { label: "Jami", value: statsData?.total_employees, icon: '👥', color: '#0ea5e9' },
          { label: "Ro'yxatdan o'tgan", value: statsData?.registered_on_bot, icon: '✅', color: '#10b981' },
          { label: "O'tmagan", value: statsData?.not_registered, icon: '⏳', color: '#f59e0b' },
        ].map(({ label, value, icon, color }, i) => (
          <div key={label} className="stat-card fade-in" style={{
            animationDelay: `${i * 60}ms`, '--stat-accent': color + '22',
          }}>
            <div className="stat-icon" style={{ background: color + '18' }}>{icon}</div>
            <div className="stat-value">{value ?? <Spinner />}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Main table */}
      <Card>
        <div className="card-header">
          <span className="card-title">Xodimlar ro'yxati</span>
          <div className="page-actions">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Ism, JSHSHIR, telefon..."
            />
            <Button
              variant="primary" size="md"
              icon="➕ "
              onClick={() => setModal('create')}
            >
              Xodim qo'shish
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.results?.map((r, i) => ({ ...r, _idx: i }))}
          loading={isLoading}
          emptyState={
            <EmptyState
              icon="👥"
              title="Xodimlar topilmadi"
              description={search ? "Qidiruv natijasi bo'sh" : "Birinchi xodimni qo'shing"}
            />
          }
        />

        <Pagination
          count={data?.count || 0}
          page={page}
          onChange={setPage}
        />
      </Card>

      {/* Create / Edit Modal */}
      <EmployeeModal
        isOpen={modal !== null}
        employee={modal !== 'create' && modal !== null ? modal : null}
        onClose={() => setModal(null)}
        onSave={(d) => {
          if (modal === 'create') {
            createMutation.mutate(d);
          } else {
            updateMutation.mutate({ id: modal.id, data: d });
          }
        }}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Xodimni o'chirish"
        width={420}
      >
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          <strong style={{ color: 'var(--rose-500)' }}>{deleteTarget?.full_name}</strong> ni
          o'chirishni xohlaysizmi? Bu amalni qaytarib bo'lmaydi.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Bekor</Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(deleteTarget.id)}
          >
            O'chirish
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function EmployeeModal({ isOpen, employee, onClose, onSave, loading }) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    values: employee || {},
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { onClose(); reset(); }}
      title={employee ? "Xodimni tahrirlash" : "Yangi xodim qo'shish"}
    >
      <form onSubmit={handleSubmit(onSave)}>
        <FormField label="To'liq ism" required error={errors.full_name}>
          <input
            {...register('full_name', { required: "To'liq ism majburiy" })}
            className="form-input"
            placeholder="Abdullayev Abdulla Abdullayevich"
          />
        </FormField>

        <FormField label="JSHSHIR" required error={errors.jshshir}>
          <input
            {...register('jshshir', {
              required: "JSHSHIR majburiy",
              pattern: { value: /^\d{14}$/, message: "14 ta raqam bo'lishi kerak" },
            })}
            className="form-input"
            placeholder="31234567891011"
            maxLength={14}
          />
        </FormField>

        <div className="two-col">
          <FormField label="Lavozim" required error={errors.position}>
            <input
              {...register('position', { required: "Lavozim majburiy" })}
              className="form-input"
              placeholder="Dasturchi"
            />
          </FormField>

          <FormField label="Telefon" required error={errors.phone_number}>
            <input
              {...register('phone_number', { required: "Telefon majburiy" })}
              className="form-input"
              placeholder="+998901234567"
            />
          </FormField>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button type="button" variant="secondary" onClick={() => { onClose(); reset(); }}>
            Bekor
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            {employee ? 'Saqlash' : "Qo'shish"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
